import { isNodeSelection, type Editor } from '@tiptap/react';
import * as React from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { BlockMathIcon } from '@/components/tiptap-editor/tiptap-icons/block-math-icon';
import { InlineMathIcon } from '@/components/tiptap-editor/tiptap-icons/inline-math-icon';

// --- Lib ---
import { parseMathEditText } from '@/components/tiptap-editor/tiptap-extension/math-extension/utils';
import { isNodeInSchema } from '@/lib/tiptap-utils';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { toast } from 'sonner';

export type MathType = 'inline' | 'block';

export interface MathButtonProps extends Omit<ButtonProps, 'type'> {
    /**
     * The TipTap editor instance.
     */
    editor?: Editor | null;
    /**
     * The math type (inline or block).
     */
    mathType: MathType;
    /**
     * Optional text to display alongside the icon.
     */
    text?: string;
    /**
     * Whether the button should hide when math is not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
}

export const mathIcons = {
    inline: InlineMathIcon,
    block: BlockMathIcon,
};

export const mathShortcutKeys: Record<MathType, string> = {
    inline: 'mod+m',
    block: 'mod+shift+m',
};

export const mathContent = { latex: '\\int_a^b x^2 dx' };

export function isMathActive(editor: Editor | null, mathType: MathType): boolean {
    if (!editor) return false;

    if (mathType === 'inline') {
        return editor.isActive('inline-math');
    } else {
        return editor.isActive('block-math');
    }
}

export function insertMath(editor: Editor | null, mathType: MathType): void {
    if (!editor) return;
    const { from, to, $from, $to, empty } = editor.state.selection;
    const selectedCopyText = empty ? '' : editor.state.doc.textBetween(from, to, '');

    if (mathType === 'inline') {
        if (!selectedCopyText) {
            editor.chain().focus().selectTextblockEnd().insertInlineMath(mathContent).run();
            return;
        }

        const isSingleParentInlineSelection = $from.sameParent($to) && $from.parent.isTextblock;

        if (isSingleParentInlineSelection) {
            const parsedMathText = parseMathEditText(selectedCopyText, 'inline');
            if (parsedMathText) {
                // If the selected text already contains \(...\) do not add those again
                editor.chain().focus().deleteSelection().insertInlineMath(parsedMathText).run();
                return;
            }

            editor.chain().focus().deleteSelection().insertInlineMath({ latex: selectedCopyText }).run();
            return;
        }
        toast.warning('Invalid Selection for Inline Math', {
            description: 'Selection cannot cross block boundaries.\nPlease select the text you want to insert the math into and try again.',
        });
        editor.chain().focus().run();
    } else {
        if (!selectedCopyText) {
            editor.chain().insertBlockMath(mathContent).focus().run();
        }

        const parsedMathText = parseMathEditText(selectedCopyText, 'block');
        if (parsedMathText) {
            // If the selected text already contains \[...\] do not add those again
            editor.chain().focus().deleteSelection().insertBlockMath(parsedMathText).run();
            return;
        }

        editor.chain().focus().deleteSelection().insertBlockMath({ latex: selectedCopyText }).run();
        return;
    }
}

export function isMathButtonDisabled(editor: Editor | null, _mathType: MathType, userDisabled: boolean = false): boolean {
    if (!editor) return true;
    if (userDisabled) return true;
    return false;
}

export function shouldShowMathButton(params: {
    editor: Editor | null;
    mathType: MathType;
    hideWhenUnavailable: boolean;
    inlineMathInSchema: boolean;
    blockMathInSchema: boolean;
}): boolean {
    const { editor, mathType, hideWhenUnavailable, inlineMathInSchema, blockMathInSchema } = params;

    const mathInSchema = mathType === 'inline' ? inlineMathInSchema : blockMathInSchema;

    if (!mathInSchema || !editor) {
        return false;
    }

    if (hideWhenUnavailable) {
        if (isNodeSelection(editor.state.selection)) {
            return false;
        }
    }

    return true;
}

export function getFormattedMathName(mathType: MathType): string {
    return mathType === 'inline' ? 'Inline Math' : 'Block Math';
}

export function useMathState(editor: Editor | null, mathType: MathType, disabled: boolean = false) {
    const inlineMathInSchema = isNodeInSchema('inlineMath', editor);
    const blockMathInSchema = isNodeInSchema('blockMath', editor);
    const isDisabled = isMathButtonDisabled(editor, mathType, disabled);
    const isActive = isMathActive(editor, mathType);

    const Icon = mathIcons[mathType];
    const shortcutKey = mathShortcutKeys[mathType];
    const formattedName = getFormattedMathName(mathType);

    return {
        inlineMathInSchema,
        blockMathInSchema,
        isDisabled,
        isActive,
        Icon,
        shortcutKey,
        formattedName,
    };
}

export const MathButton = React.forwardRef<HTMLButtonElement, MathButtonProps>(
    (
        {
            editor: providedEditor,
            mathType,
            text,
            hideWhenUnavailable = false,
            className = '',
            disabled,
            onClick,
            children,
            ...buttonProps
        },
        ref,
    ) => {
        const { editor } = useTiptapEditor(providedEditor);

        const { inlineMathInSchema, blockMathInSchema, isDisabled, isActive, Icon, formattedName } = useMathState(
            editor,
            mathType,
            disabled,
        );

        const handleClick = React.useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                onClick?.(e);

                if (!e.defaultPrevented && !isDisabled && editor) {
                    insertMath(editor, mathType);
                }
            },
            [onClick, isDisabled, editor, mathType],
        );

        const show = React.useMemo(() => {
            return shouldShowMathButton({
                editor,
                mathType,
                hideWhenUnavailable,
                inlineMathInSchema,
                blockMathInSchema,
            });
        }, [editor, mathType, hideWhenUnavailable, inlineMathInSchema, blockMathInSchema]);

        if (!show || !editor || !editor.isEditable) {
            return null;
        }

        return (
            <Button
                type="button"
                className={className.trim()}
                disabled={isDisabled}
                data-style="ghost"
                data-active-state={isActive ? 'on' : 'off'}
                data-disabled={isDisabled}
                role="button"
                tabIndex={-1}
                aria-label={formattedName}
                aria-pressed={isActive}
                tooltip={formattedName}
                onClick={handleClick}
                {...buttonProps}
                ref={ref}
            >
                {children || (
                    <>
                        <Icon className="tiptap-button-icon" />
                        {text && <span className="tiptap-button-text">{text}</span>}
                    </>
                )}
            </Button>
        );
    },
);

MathButton.displayName = 'MathButton';

export default MathButton;
